import type { LensCardData } from '~/types/leLens';

import { config } from './config';
import { LEGACY_REGIONS, financialReadings } from './legacy';
import { type Reading, compareReadings, numeric, rowsFor } from './liveModel';

export const CHART_YEARS = ['2023', '2024', '2025', '2026'];
export const CHART_GRAINS = ['month', 'quarter', 'year'];
export function calendarWindow(year: string, prior = false) {
  const value = Number(year) - (prior ? 1 : 0);
  return { from: `${value}-01-01`, to: `${value}-12-31` };
}
export function financialViewProblem(cards: Array<LensCardData>, view: string, comparison: boolean) {
  const ids =
    view === 'global-mtd'
      ? ['global_summary']
      : view === 'comparison'
        ? ['summary', 'global_summary']
        : view === 'comparison-chart'
          ? ['calendar', 'calendar_global']
          : [];
  for (const id of ids) {
    const card = cards.find((item) => item.cardId === id);
    if (!card || !['ready', 'empty'].includes(card.status) || card.truncated)
      return `${id}: complete financial source data unavailable. Charts, totals and exports are withheld.`;
    if (
      comparison &&
      (!card.comparison || !['ready', 'empty'].includes(card.comparison.status) || card.comparison.truncated)
    )
      return `${id}: complete financial comparison data unavailable. Charts, totals and exports are withheld.`;
  }
  return undefined;
}
export function globalFinancialRows(card: LensCardData | undefined, prior = false): Array<Reading> {
  const rows = rowsFor(card, config.ratios, prior).filter(
    (row) => numeric(row.source_row_count) !== null && Number(row.source_row_count) > 0,
  );
  return financialReadings(rows.map((row) => ({ ...row, region: 'Global - All Markets' })));
}
export function marketOverview(current: Array<Reading>, prior: Array<Reading>, comparison: boolean): Array<Reading> {
  const readings = comparison ? compareReadings(current, prior, ['region'], true) : current;
  const metrics = [
    ...new Set([...current, ...prior].flatMap((row) => Object.keys(row).filter((key) => key !== 'region'))),
  ];
  return LEGACY_REGIONS.map(
    (region) =>
      readings.find((row) => row.region === region) ?? {
        region,
        ...Object.fromEntries(metrics.map((key) => [key, null])),
      },
  );
}

/** Align CY and full PY to the same calendar bucket without filling absent current observations. */
export function calendarSeries(current: Array<Reading>, prior: Array<Reading>, grain: string, metric: string) {
  const key = (row: Reading) =>
    grain === 'year'
      ? 'Year'
      : grain === 'quarter'
        ? `Q${Math.floor((Number(String(row.chart_bucket).slice(5, 7)) - 1) / 3) + 1}`
        : String(row.chart_bucket).slice(5, 7);
  const now = new Map(current.map((row) => [key(row), row]));
  const old = new Map(prior.map((row) => [key(row), row]));
  const buckets = [...new Set([...now.keys(), ...old.keys()])].sort();
  return {
    buckets,
    current: buckets.map((bucket) =>
      metric === 'margin_aud_ar' && now.has(bucket)
        ? (numeric(now.get(bucket)?.[metric]) ?? 0)
        : numeric(now.get(bucket)?.[metric]),
    ),
    prior: buckets.map((bucket) =>
      metric === 'margin_aud_ar' && old.has(bucket)
        ? (numeric(old.get(bucket)?.[metric]) ?? 0)
        : numeric(old.get(bucket)?.[metric]),
    ),
  };
}

/** Combine selected markets' raw operands before applying non-additive financial formulas. */
export function groupCalendar(rows: Array<Reading>): Array<Reading> {
  const groups = new Map<string, Reading>();
  for (const row of rows) {
    const bucket = String(row.chart_bucket);
    const total = groups.get(bucket) ?? { chart_bucket: bucket };
    for (const [key, raw] of Object.entries(row)) {
      if (key === 'chart_bucket' || key === 'region') continue;
      const value = numeric(raw);
      if (!(key in total)) total[key] = null;
      if (value !== null) total[key] = (numeric(total[key]) ?? 0) + value;
    }
    groups.set(bucket, total);
  }
  return [...groups.values()];
}
